import {
    ENVIRONMENT_PROD,
    EVENT_MODULES_LOADED,
    MODULE_APPLEPAY,
    MODULE_CORE,
    MODULE_GOOGLEPAY,
    MODULE_LOAD_FAILED,
    MODULE_LOAD_SUCCESSFULLY,
    MODULE_LOADER,
    moduleApplePay,
    moduleCore,
    moduleGooglePay,
    TIMEOUT_LOAD_DEFAULT,
    TIMEOUT_LOAD_MAX,
    TIMEOUT_LOAD_MIN,
} from '../../constants';
import {
    createScriptElement,
    doesModuleAlreadyExist,
    getElementData,
    getModuleMountObject,
    resolveEnvironment,
    resolveUrl,
    resolveVersion,
    setElementData,
    validateNumber,
} from '../../Function';
import { buildElementId } from '../../Function/buildElementId';
import { TLoadComgateCheckoutConfig, TLoadComgateCheckoutResult, TModule, TModuleResponse, TResolvedVersion } from '../../types';
import { preloadComgateCheckout } from '../ModulePreload';
import { loadConfigValidator } from './loadConfigValidator';

interface THandleCoreLoadingParams {
    event: Event | null;
    resolve: (value: TLoadComgateCheckoutResult) => void;
    reject: (value: TLoadComgateCheckoutResult) => void;
    coreLoading: Promise<TModuleResponse>;
    frozenConfig: TLoadComgateCheckoutConfig;
    cdnBaseUrl: string;
    resolvedVersion: TResolvedVersion;
}

/**
 * Loads the Comgate Checkout SDK based on the provided configuration.
 *
 * @param {TLoadComgateCheckoutConfig} config - The configuration object for initializing the Comgate Checkout.
 *      This includes properties like `checkoutId`, environments, and other needed parameters for setup.
 * @return {Promise<TLoadComgateCheckoutResult>} A promise that resolves with the result of the Comgate Checkout
 *      initialization operation. The result contains success status, checkoutId, and error information if initialization fails.
 */
export async function loadComgateCheckout(config: TLoadComgateCheckoutConfig): Promise<TLoadComgateCheckoutResult> {
    // Validate base config
    const validationResponse = loadConfigValidator(config);
    if (validationResponse !== true) {
        return Promise.reject({
            success: false,
            checkoutId: config?.checkoutId,
            error: {
                [MODULE_LOADER]: new TypeError(validationResponse),
            },
        } as TLoadComgateCheckoutResult);
    }

    const frozenConfig: TLoadComgateCheckoutConfig = Object.freeze(JSON.parse(JSON.stringify(config)));

    // Const debugPrinter = makeDebugPrinter('Load Debug', frozenConfig, { bg: 'red' });
    // DebugPrinter.print('config OK');

    // Resolve and set CDN base url to window
    const env = resolveEnvironment(frozenConfig);
    const cdnBaseUrl = resolveUrl(frozenConfig, 'cdn');
    const apiBaseUrl = resolveUrl(frozenConfig, 'api');

    // DebugPrinter.print('resolved env, cdnUrl, apiUrn', { env, cdnBaseUrl, apiBaseUrl });

    (window.comgate ??= {}).checkout ??= {};
    window.comgate.checkout.checkoutId = frozenConfig.checkoutId;

    if (env !== ENVIRONMENT_PROD) {
        window.comgate.checkout.config ??= {};
        window.comgate.checkout.config.baseCdnUrl = cdnBaseUrl;
        window.comgate.checkout.config.baseApiUrl = apiBaseUrl;
        window.comgate.checkout.config.environment = env;
        // DebugPrinter.print('ENV není DEV, mapuji baseCdnUrl a baseApiUrl na window.comgate.checkout.config', window.comgate);
    }

    const resolvedVersion: TResolvedVersion = await resolveVersion(frozenConfig, cdnBaseUrl);
    // Console.log('2 - resolved version', resolvedVersion);

    // TODO: jistě budou existovat i modulu, které CORE nepotřebují, to by se časem mělo vyřešit
    // Initialize the results array and load Core module

    return new Promise<TLoadComgateCheckoutResult>((resolve, reject) => {
        let coreLoading: Promise<TModuleResponse> | null = null;

        // The CORE module is already loaded, has been found in the DOM => we can therefore proceed without loading it
        if (doesModuleAlreadyExist(moduleCore)) {
            coreLoading = Promise.resolve({
                success: true,
                name: moduleCore.name,
                mount: getModuleMountObject(moduleCore),
            });
            handleCoreLoading({ event: null, resolve, reject, coreLoading, frozenConfig, cdnBaseUrl, resolvedVersion });
            return;
        }

        // Register an event listener for ComgateCheckoutCoreLoaded
        // The CORE module has not been loaded before, and it is necessary to wait for the event that indicates the module has been successfully loaded and executed
        document.addEventListener(moduleCore.events.ready, (event: Event) => {
            // Console.log('5 - Event zachycen', event);
            if (coreLoading) {
                handleCoreLoading({ event, resolve, reject, coreLoading, frozenConfig, cdnBaseUrl, resolvedVersion });
            } else {
                // If coreLoading is not defined, resolve immediately
                reject({
                    checkoutId: frozenConfig.checkoutId,
                    success: false,
                    error: {
                        [MODULE_CORE]: new Error('Failed to handle Core module loading.'),
                    },
                });
            }
        });

        // The CORE must be loaded first, and only then can the modules be loaded
        coreLoading = load(frozenConfig, moduleCore, cdnBaseUrl, resolvedVersion).catch((error) => {
            reject({
                checkoutId: frozenConfig.checkoutId,
                success: false,
                error: {
                    [MODULE_CORE]: new Error('Failed to handle Core module loading.', { cause: JSON.stringify(error) }),
                },
            });
            return Promise.resolve({
                success: false,
                name: MODULE_CORE,
                error: new Error('Failed to handle Core module loading.', { cause: error }),
            });
        });

        // To prevent modules from blocking while waiting for the CORE to load, we initiate module preloading in advance, before the load is triggered
        preloadComgateCheckout({
            ...config,
            excludeCore: true,
        });
    });
}

/**
 * Handles the loading process of core and optional modules for a specific checkout experience.
 *
 * This asynchronous function performs the following steps:
 * 1. Initializes the loading process by pushing the core module loading promise to a tracking array.
 * 2. Checks for the presence of certain modules (Apple Pay and Google Pay) in the provided configuration.
 * 3. Asynchronously loads the modules if specified, appending their loading promises to the tracking array.
 * 4. Awaits and tracks the resolution statuses of all loaded modules.
 * 5. Constructs a response object indicating the success or failure status of the loading process.
 * 6. Executes the defined `onLoad` callback if present in the configuration, passing the response object.
 * 7. Emits a global browser event signaling the readiness of the modules.
 * 8. Resolves or rejects the primary promise based on the loading operation's overall success.
 *
 * @param {THandleCoreLoadingParams} parameters - The parameters required for initiating the loading process.
 * @property {object} parameters.frozenConfig - The frozen configuration object containing setup data.
 * @property {string} parameters.frozenConfig.checkoutId - The unique identifier for the checkout.
 * @property {Array<string>} [parameters.frozenConfig.modules] - Optional array of additional module names to load.
 * @property {Function} [parameters.frozenConfig.onLoad] - Optional callback function to handle the load completion status.
 * @property {Function} parameters.resolve - Function to resolve the main promise with the loading results.
 * @property {Function} parameters.reject - Function to reject the main promise with an error result.
 * @property {Promise} parameters.coreLoading - A promise tracking the core module's loading status.
 * @property {string} parameters.cdnBaseUrl - Base URL for loading module assets.
 * @property {string} parameters.resolvedVersion - A string representing the version of the integration.
 */
const handleCoreLoading = async (parameters: THandleCoreLoadingParams) => {
    const loadResults = [parameters.coreLoading];

    // Console.log('6 - Spouštím akce po úspěšném zachycení spuštěněho CORE', parameters);

    // Console.log('8 - Jdu loadovat moduly AP a GP', parameters.frozenConfig.modules);
    if (parameters.frozenConfig.modules !== undefined) {
        // Load Apple Pay module
        if (parameters.frozenConfig.modules.includes(MODULE_APPLEPAY)) {
            // Console.log('8.1 - Loaduji Apple Pay');
            loadResults.push(load(parameters.frozenConfig, moduleApplePay, parameters.cdnBaseUrl, parameters.resolvedVersion));
        }

        // Load Google Pay module
        if (parameters.frozenConfig.modules.includes(MODULE_GOOGLEPAY)) {
            // Console.log('8.1 - Loaduji Google Pay');
            loadResults.push(load(parameters.frozenConfig, moduleGooglePay, parameters.cdnBaseUrl, parameters.resolvedVersion));
        }
    }
    // Console.log('9 - Zahajuji čekání, až budou moduly ready', loadResults);

    const modulesStatus = await Promise.all(loadResults);

    // Console.log('10 - Všechny moduly jsou nyní ready - promises resolved', modulesStatus);

    const loadResponse: TLoadComgateCheckoutResult = {
        checkoutId: parameters.frozenConfig.checkoutId,
        success: true,
    };

    // Console.log('11 - Sestavuji resolve message', modulesStatus);
    modulesStatus.forEach((module) => {
        if (module.success) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            loadResponse[module.name] = module.mount!;
        } else {
            loadResponse.success = false;
            loadResponse.error = loadResponse.error || {};
            loadResponse.error[module.name] = module.error!;
        }
    });

    if (parameters.frozenConfig.onLoad !== undefined && typeof parameters.frozenConfig.onLoad === 'function') {
        // Console.log('11.5 - Volám onLoad', loadResponse);
        parameters.frozenConfig.onLoad(loadResponse);
    }

    // Console.log('12 - Emituji even o stavu READY', EVENT_MODULES_LOADED);
    window.dispatchEvent(new Event(EVENT_MODULES_LOADED));

    if (loadResponse.success) {
        parameters.resolve(loadResponse);
    } else {
        parameters.reject(loadResponse);
    }
};

/**
 * Asynchronously loads a module script with the specified configuration, adding it to the document
 * and resolving or rejecting the returned promise based on the load success or failure.
 *
 * @param {TLoadComgateCheckoutConfig} config - Configuration object used for loading the module. Includes properties such as `async`, `defer`, and `timeout`.
 * @param {TModule} module - The module to be loaded, defined by its name, file path, and associated events.
 * @param {string} cdnBaseUrl - Base URL for the content delivery network to resolve the script location.
 * @param {TResolvedVersion} resolvedVersion - Specific version of the module to load, ensuring compatibility.
 * @returns {Promise<TModuleResponse>} A promise resolving with the module response, containing the success status, module name, and mount object if loading was successful. The promise rejects with an error on failure.
 *
 * The function:
 * - Handles configuration options for `async` and `defer` execution of the script.
 * - Creates or retrieves a script element associated with the module.
 * - Defines error handling and timeout behaviors to manage edge cases during script loading.
 * - Listens for the specified `ready` event emitted by the module after successful loading.
 * - Ensures proper cleanup of timeouts and resources on completion or error.
 */
const load = async (
    config: TLoadComgateCheckoutConfig,
    module: TModule,
    cdnBaseUrl: string,
    resolvedVersion: TResolvedVersion
): Promise<TModuleResponse> =>
    new Promise((resolve, reject) => {
        let resolved = false;
        let timeoutId: number | undefined;
        let isAsync = true;
        let isDeferred = false;
        let element: HTMLElementTagNameMap['script'] | null = null;
        const isAsyncDefined = 'async' in config;
        const isDeferDefined = 'defer' in config;

        try {
            // Set async and defer values if defined
            if (isAsyncDefined || isDeferDefined) {
                if (config.async === true) {
                    isAsync = true;
                    isDeferred = false;
                } else if (config.defer === true) {
                    isAsync = false;
                    isDeferred = true;
                } else {
                    if (isAsyncDefined) {
                        isAsync = Boolean(config.async);
                    }
                    if (isDeferDefined) {
                        isDeferred = Boolean(config.defer);
                    }
                }
            }

            // -${hash ? `-${hash}` : ''}
            const loadId = buildElementId('load', module.name);
            element = document.querySelector<HTMLElementTagNameMap['script']>(`#${loadId}`);

            if (element) {
                if (getElementData(element, 'success') === MODULE_LOAD_SUCCESSFULLY) {
                    setElementData(element, 'success', resolveModuleLoad(resolve, module) ? MODULE_LOAD_SUCCESSFULLY : MODULE_LOAD_FAILED);
                } else {
                    // Try to reload the script if it was not loaded correctly
                    element.remove();
                    element = null;
                }
            }

            if (!element) {
                const script = createScriptElement(loadId, resolvedVersion, isAsync, isDeferred, cdnBaseUrl, module.file);

                // Check if the script is not loaded => error during loading
                script.addEventListener('error', (error) => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        setElementData(script, 'success', MODULE_LOAD_FAILED);
                        reject({
                            success: false,
                            name: module.name,
                            error: `Failed to load script: ${JSON.stringify(error)}`,
                        });
                    }
                });

                // Set timeout for the case when Comgate Checkout is not loaded
                const timeout = validateNumber(config.timeout, TIMEOUT_LOAD_MIN, TIMEOUT_LOAD_MAX, TIMEOUT_LOAD_DEFAULT);

                // Define timeout for the case when Comgate Checkout is not loaded
                timeoutId = window.setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        const mountObject = getModuleMountObject(module);
                        if (!mountObject) {
                            setElementData(script, 'success', MODULE_LOAD_FAILED);
                            reject({
                                success: false,
                                name: module.name,
                                error: new Error('Timeout reached.'),
                            });
                            return;
                        }

                        setElementData(script, 'success', MODULE_LOAD_SUCCESSFULLY);
                        resolve({
                            success: true,
                            name: module.name,
                            mount: mountObject,
                        });
                    }
                }, timeout);

                // Wait for Ready event
                document.addEventListener(module.events.ready, () => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        setElementData(script, 'success', resolveModuleLoad(resolve, module) ? MODULE_LOAD_SUCCESSFULLY : MODULE_LOAD_FAILED);
                    }
                });

                document.head.append(script);
            } else if (!resolved) {
                resolved = true;
                clearTimeout(timeoutId);
                setElementData(element, 'success', resolveModuleLoad(resolve, module) ? MODULE_LOAD_SUCCESSFULLY : MODULE_LOAD_FAILED);
            }
        } catch (error) {
            if (!resolved) {
                resolved = true;
                setElementData(element, 'success', MODULE_LOAD_FAILED);
                reject({
                    success: false,
                    name: module.name,
                    error: new Error('Unexpected error.', { cause: error }),
                });
            }
        }
    });

/**
 * Resolves the loading of a specified module and invokes a callback function with the result.
 *
 * The function determines whether the specified module can be properly mounted by checking
 * the module's mount point object. If the module cannot be mounted, the callback function is
 * invoked with an error response and the function returns false. Otherwise, the callback is
 * invoked with the success response containing the module's mount object, and the function
 * returns true.
 *
 * @param {function(TModuleResponse): void} onResolve - A callback function invoked upon
 *     resolving the module's loading status. It is passed an object containing the resolution
 *     result.
 * @param {TModule} module - The module object containing the necessary information regarding
 *     the module to be loaded and mounted.
 * @returns {boolean} - Returns true if the module is successfully resolved and can be mounted;
 *     otherwise, returns false.
 */
const resolveModuleLoad = (onResolve: (value: TModuleResponse) => void, module: TModule): boolean => {
    const mountObject = getModuleMountObject(module);
    if (!mountObject) {
        onResolve({
            success: false,
            name: module.name,
            error: new Error(`The module does not mount correctly. Path window.${module.mountPoint.join('.')} is undefined.`),
        });
        return false;
    }
    onResolve({
        success: true,
        name: module.name,
        mount: mountObject,
    });
    return true;
};
